import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletGoalPlannerPage.css";

type GoalRow = {
  goalId:string;
  goalType:string;
  status:string;
  title:string;
  description?:string|null;
  targetOn?:string|null;
  effectiveAt:string;
  authorship:"LEARNER"|"SCHOOL";
  editable:boolean;
};

type ActionRow = {
  entryId:string;
  title:string;
  body?:string|null;
  actionStatus:"ACTIVE"|"COMPLETED"|"CANCELLED";
  actionOn?:string|null;
  occurredAt:string;
  authorship:"LEARNER"|"SCHOOL";
  editable:boolean;
};

type PlannerData = {
  learnerId:string;
  journeyId?:string|null;
  goals:GoalRow[];
  nextActions:ActionRow[];
  counts:{activeGoals:number;activeNextActions:number};
};

const goalTypeOptions=[
  ["EXPLORATION","탐색"],
  ["LEARNING","학습"],
  ["EXPERIENCE","경험"],
  ["EDUCATION","진학"],
  ["CAREER","진로"],
  ["OTHER","기타"]
] as const;

function formatDate(value?:string|null){
  if(!value)return "-";
  const date=new Date(value);
  if(Number.isNaN(date.getTime()))return value;
  return date.toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}

function goalTypeLabel(value:string){
  return goalTypeOptions.find(([key])=>key===value)?.[1] || value;
}

function goalStatusLabel(value:string){
  const labels:Record<string,string>={
    DRAFT:"초안",ACTIVE:"진행 중",ACHIEVED:"달성",PAUSED:"잠시 멈춤",RETIRED:"종료"
  };
  return labels[value] || value;
}

function actionStatusLabel(value:string){
  const labels:Record<string,string>={
    ACTIVE:"할 일",COMPLETED:"완료",CANCELLED:"취소"
  };
  return labels[value] || value;
}

export function WalletGoalPlannerPage({
  client,
  onUseInPortfolio
}:{
  client:PlatformClient;
  onUseInPortfolio:(goalId:string)=>void;
}){
  const [data,setData]=useState<PlannerData|null>(null);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [busy,setBusy]=useState(false);

  const [goalType,setGoalType]=useState("EXPLORATION");
  const [goalTitle,setGoalTitle]=useState("");
  const [goalDescription,setGoalDescription]=useState("");
  const [goalTargetOn,setGoalTargetOn]=useState("");

  const [actionTitle,setActionTitle]=useState("");
  const [actionBody,setActionBody]=useState("");
  const [actionOn,setActionOn]=useState("");

  async function load(){
    const {data,error}=await client.rpc("wallet_goal_planner");
    if(error){setError(error.message);return;}
    setData(data as PlannerData);
    setError("");
  }

  useEffect(()=>{void load();},[client]);

  const currentGoals=useMemo(
    ()=>data?.goals.filter((item)=>["ACTIVE","PAUSED","DRAFT"].includes(item.status)) || [],
    [data]
  );
  const pastGoals=useMemo(
    ()=>data?.goals.filter((item)=>["ACHIEVED","RETIRED"].includes(item.status)) || [],
    [data]
  );
  const activeActions=useMemo(
    ()=>data?.nextActions.filter((item)=>item.actionStatus==="ACTIVE") || [],
    [data]
  );
  const pastActions=useMemo(
    ()=>data?.nextActions.filter((item)=>item.actionStatus!=="ACTIVE") || [],
    [data]
  );

  async function createGoal(){
    if(!goalTitle.trim())return;
    setBusy(true);setNotice("");setError("");
    const {error}=await client.rpc("wallet_create_goal",{
      p_goal_type:goalType,
      p_title:goalTitle.trim(),
      p_description:goalDescription.trim() || null,
      p_target_on:goalTargetOn || null,
      p_status:"ACTIVE"
    });
    if(error){setError(error.message);setBusy(false);return;}
    setGoalTitle("");setGoalDescription("");setGoalTargetOn("");
    setNotice("새 목표를 추가했습니다.");
    await load();setBusy(false);
  }

  async function transitionGoal(goal:GoalRow,status:string){
    setBusy(true);setNotice("");setError("");
    const {error}=await client.rpc("wallet_supersede_goal",{
      p_goal_id:goal.goalId,
      p_status:status,
      p_title:goal.title,
      p_description:goal.description || null,
      p_target_on:goal.targetOn || null
    });
    if(error){setError(error.message);setBusy(false);return;}
    setNotice(status==="ACHIEVED" ? "목표를 달성으로 기록했습니다." : "목표 상태를 변경했습니다.");
    await load();setBusy(false);
  }

  async function createAction(){
    if(!actionTitle.trim())return;
    setBusy(true);setNotice("");setError("");
    const {error}=await client.rpc("wallet_create_next_action",{
      p_title:actionTitle.trim(),
      p_body:actionBody.trim() || null,
      p_action_on:actionOn || null
    });
    if(error){setError(error.message);setBusy(false);return;}
    setActionTitle("");setActionBody("");setActionOn("");
    setNotice("다음 행동을 추가했습니다.");
    await load();setBusy(false);
  }

  async function transitionAction(action:ActionRow,status:"COMPLETED"|"CANCELLED"){
    setBusy(true);setNotice("");setError("");
    const {error}=await client.rpc("wallet_supersede_next_action",{
      p_entry_id:action.entryId,
      p_title:action.title,
      p_body:action.body || null,
      p_action_on:action.actionOn || null,
      p_action_status:status
    });
    if(error){setError(error.message);setBusy(false);return;}
    setNotice(status==="COMPLETED" ? "다음 행동을 완료했습니다." : "다음 행동을 취소했습니다.");
    await load();setBusy(false);
  }

  if(!data && !error)return <p className="muted">목표와 다음 행동을 불러오는 중…</p>;

  return (
    <section className="wallet-page goal-planner-page">
      <div className="goal-planner-hero">
        <div>
          <p className="eyebrow">GOAL & NEXT ACTION</p>
          <h2>목표를 세우고, 오늘의 한 걸음을 정해요</h2>
          <p>학교에서 함께 세운 목표는 그대로 보고, 내가 만든 목표와 다음 행동은 직접 관리할 수 있습니다.</p>
        </div>
        <div className="goal-planner-counts">
          <div><span>진행 목표</span><strong>{data?.counts.activeGoals ?? 0}</strong></div>
          <div><span>다음 행동</span><strong>{data?.counts.activeNextActions ?? 0}</strong></div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="goal-planner-notice">{notice}</p>}

      <div className="goal-planner-grid">
        <section className="goal-planner-panel">
          <header>
            <div>
              <p className="eyebrow">MY GOALS</p>
              <h3>나의 목표</h3>
            </div>
            <span>{currentGoals.length}개 진행</span>
          </header>

          <div className="goal-create-card">
            <div className="goal-form-row">
              <label>
                목표 종류
                <select value={goalType} onChange={(e)=>setGoalType(e.target.value)}>
                  {goalTypeOptions.map(([value,label])=><option value={value} key={value}>{label}</option>)}
                </select>
              </label>
              <label>
                목표일
                <input type="date" value={goalTargetOn} onChange={(e)=>setGoalTargetOn(e.target.value)} />
              </label>
            </div>
            <label>
              목표
              <input value={goalTitle} maxLength={200} onChange={(e)=>setGoalTitle(e.target.value)} placeholder="예: 관심 직업 3가지를 직접 조사해보기" />
            </label>
            <label>
              이유 또는 메모
              <textarea value={goalDescription} onChange={(e)=>setGoalDescription(e.target.value)} placeholder="왜 이 목표가 중요한지 짧게 적어도 좋아요." />
            </label>
            <button className="primary" disabled={busy || !goalTitle.trim()} onClick={createGoal}>목표 추가</button>
          </div>

          <div className="goal-card-list">
            {currentGoals.length ? currentGoals.map((goal)=>(
              <article className="goal-plan-card" key={goal.goalId}>
                <div className="goal-plan-card-head">
                  <div>
                    <span>{goal.authorship==="LEARNER" ? "내가 세운 목표" : "학교와 함께 세운 목표"}</span>
                    <h4>{goal.title}</h4>
                  </div>
                  <em>{goalStatusLabel(goal.status)}</em>
                </div>
                <div className="goal-plan-meta">
                  <span>{goalTypeLabel(goal.goalType)}</span>
                  <span>목표일 {formatDate(goal.targetOn)}</span>
                </div>
                {goal.description && <p>{goal.description}</p>}
                <div className="goal-plan-actions">
                  <button onClick={()=>onUseInPortfolio(goal.goalId)}>문서에 활용</button>
                  {goal.editable ? (
                    <>
                      {goal.status!=="PAUSED" && <button disabled={busy} onClick={()=>transitionGoal(goal,"PAUSED")}>잠시 멈춤</button>}
                      {goal.status==="PAUSED" && <button disabled={busy} onClick={()=>transitionGoal(goal,"ACTIVE")}>다시 시작</button>}
                      <button disabled={busy} onClick={()=>transitionGoal(goal,"ACHIEVED")}>달성</button>
                      <button disabled={busy} onClick={()=>transitionGoal(goal,"RETIRED")}>종료</button>
                    </>
                  ) : <span className="goal-readonly-note">학교 기록 · 읽기 전용</span>}
                </div>
              </article>
            )):<div className="goal-empty">진행 중인 목표가 없습니다. 작은 목표 하나부터 시작해보세요.</div>}
          </div>

          {pastGoals.length>0 && (
            <details className="goal-history">
              <summary>지난 목표 {pastGoals.length}개</summary>
              {pastGoals.map((goal)=>(
                <div key={goal.goalId}><strong>{goal.title}</strong><span>{goalStatusLabel(goal.status)} · {formatDate(goal.targetOn)}</span></div>
              ))}
            </details>
          )}
        </section>

        <section className="goal-planner-panel">
          <header>
            <div>
              <p className="eyebrow">NEXT ACTION</p>
              <h3>다음 행동</h3>
            </div>
            <span>{activeActions.length}개 할 일</span>
          </header>

          <div className="goal-create-card">
            <label>
              다음에 할 한 가지
              <input value={actionTitle} maxLength={200} onChange={(e)=>setActionTitle(e.target.value)} placeholder="예: 진로체험 신청서 작성하기" />
            </label>
            <label>
              메모
              <textarea value={actionBody} onChange={(e)=>setActionBody(e.target.value)} placeholder="필요한 준비물이나 방법을 적어보세요." />
            </label>
            <label>
              실행 예정일
              <input type="date" value={actionOn} onChange={(e)=>setActionOn(e.target.value)} />
            </label>
            <button className="primary" disabled={busy || !actionTitle.trim()} onClick={createAction}>다음 행동 추가</button>
          </div>

          <div className="goal-card-list">
            {activeActions.length ? activeActions.map((action)=>(
              <article className="goal-plan-card action-card" key={action.entryId}>
                <div className="goal-plan-card-head">
                  <div>
                    <span>{action.authorship==="LEARNER" ? "내가 정한 다음 행동" : "학교와 함께 정한 행동"}</span>
                    <h4>{action.title}</h4>
                  </div>
                  <em>{actionStatusLabel(action.actionStatus)}</em>
                </div>
                {action.body && <p>{action.body}</p>}
                <div className="goal-plan-meta"><span>예정일 {formatDate(action.actionOn)}</span></div>
                <div className="goal-plan-actions">
                  {action.editable ? (
                    <>
                      <button className="goal-complete-button" disabled={busy} onClick={()=>transitionAction(action,"COMPLETED")}>완료</button>
                      <button disabled={busy} onClick={()=>transitionAction(action,"CANCELLED")}>취소</button>
                    </>
                  ) : <span className="goal-readonly-note">학교 기록 · 읽기 전용</span>}
                </div>
              </article>
            )):<div className="goal-empty">아직 정해둔 다음 행동이 없습니다.</div>}
          </div>

          {pastActions.length>0 && (
            <details className="goal-history">
              <summary>완료·취소한 행동 {pastActions.length}개</summary>
              {pastActions.map((action)=>(
                <div key={action.entryId}><strong>{action.title}</strong><span>{actionStatusLabel(action.actionStatus)} · {formatDate(action.actionOn)}</span></div>
              ))}
            </details>
          )}
        </section>
      </div>

      <div className="goal-planner-trust">
        <strong>기록의 의미</strong>
        <span>목표와 다음 행동은 계획 기록입니다. 완료했다고 해서 자동으로 역량·성취·Life Stamp가 생성되지는 않습니다.</span>
      </div>
    </section>
  );
}
